import type { Rect } from './frames'

/**
 * Alignment guides and snapping, for things being dragged.
 * -------------------------------------------------------------
 * Pure arithmetic; the board feeds it the box of what is being dragged (the
 * whole selection's box when several are) and the boxes of everything else
 * on the page, and applies the correction it answers (ProjectCanvas,
 * snapDrag). Two kinds of snap on each axis, the smaller correction winning:
 *
 *  - ALIGNMENT: the left edges, middles or right edges line up with
 *    something else's -- and the top edges, middles or bottoms. A line is
 *    drawn from the one to the other.
 *  - EQUAL SPACING: the gap to a neighbour becomes the same as the gap on
 *    its other side, or the same as the gap between the two things next to
 *    it -- the way Miro lets a row be spaced evenly by eye. Both gaps are
 *    marked.
 *
 * Only within `reach`, which the board sets to a few pixels on the screen, so
 * nothing jumps from far away and a deliberate position a little off is
 * still possible. Ctrl held turns it off (the board's business).
 */

export interface Segment {
  x1: number
  y1: number
  x2: number
  y2: number
}

export interface Snap {
  dx: number
  dy: number
  /** Alignment lines. */
  lines: Segment[]
  /** Equal gaps, each drawn as a short span with ends. */
  gaps: Segment[]
}

/** Everything below works on the x axis; y is the same with the boxes turned. */
interface Span {
  a: number
  b: number
  /** The other axis, for deciding what is beside what and where to draw. */
  c: number
  d: number
}

const across = (r: Rect): Span => ({ a: r.x, b: r.x + r.w, c: r.y, d: r.y + r.h })
const down = (r: Rect): Span => ({ a: r.y, b: r.y + r.h, c: r.x, d: r.x + r.w })

interface AxisSnap {
  shift: number
  kind: 'align' | 'space'
  /** Where on this axis the aligned line is, for drawing. */
  at?: number
  /** Which of the others it lines up with: the one the line is drawn to. */
  to?: number
  /** The equal gaps, as [from, to] on this axis and a place on the other. */
  gaps?: { from: number; to: number; on: number }[]
}

function alignOn(moving: Span, others: Span[], reach: number): AxisSnap | null {
  const mine = [moving.a, (moving.a + moving.b) / 2, moving.b]
  let best: AxisSnap | null = null
  // `others` arrive nearest first, so on a tie the nearest wins.
  others.forEach((other, index) => {
    for (const theirs of [other.a, (other.a + other.b) / 2, other.b]) {
      for (const ours of mine) {
        const shift = theirs - ours
        if (Math.abs(shift) <= reach && (!best || Math.abs(shift) < Math.abs(best.shift) - 0.01)) {
          best = { shift, kind: 'align', at: theirs, to: index }
        }
      }
    }
  })
  return best
}

function spaceOn(moving: Span, others: Span[], reach: number): AxisSnap | null {
  // Only things beside it: overlapping it on the other axis.
  const beside = others.filter((o) => o.c < moving.d && o.d > moving.c)
  const middle = (moving.a + moving.b) / 2
  const size = moving.b - moving.a
  const before = beside
    .filter((o) => (o.a + o.b) / 2 < middle && o.b <= moving.a + reach)
    .sort((p, q) => q.b - p.b)
  const after = beside
    .filter((o) => (o.a + o.b) / 2 > middle && o.a >= moving.b - reach)
    .sort((p, q) => p.a - q.a)
  const on = (x: Span, y: Span) => (Math.max(x.c, y.c) + Math.min(x.d, y.d)) / 2
  const options: AxisSnap[] = []

  const A = before[0]
  const B = after[0]
  // Halfway between the two neighbours.
  if (A && B) {
    const start = (A.b + B.a - size) / 2
    if (start > A.b) {
      options.push({
        shift: start - moving.a,
        kind: 'space',
        gaps: [
          { from: A.b, to: start, on: on(A, moving) },
          { from: start + size, to: B.a, on: on(B, moving) },
        ],
      })
    }
  }
  // The same gap as between the neighbour and the one beyond it.
  const A2 = A && beside.filter((o) => o.b <= A.a).sort((p, q) => q.b - p.b)[0]
  if (A && A2) {
    const gap = A.a - A2.b
    options.push({
      shift: A.b + gap - moving.a,
      kind: 'space',
      gaps: [
        { from: A2.b, to: A.a, on: on(A2, A) },
        { from: A.b, to: A.b + gap, on: on(A, moving) },
      ],
    })
  }
  const B2 = B && beside.filter((o) => o.a >= B.b).sort((p, q) => p.a - q.a)[0]
  if (B && B2) {
    const gap = B2.a - B.b
    options.push({
      shift: B.a - gap - size - moving.a,
      kind: 'space',
      gaps: [
        { from: B.a - gap, to: B.a, on: on(B, moving) },
        { from: B.b, to: B2.a, on: on(B, B2) },
      ],
    })
  }
  let best: AxisSnap | null = null
  for (const option of options) {
    if (Math.abs(option.shift) <= reach && (!best || Math.abs(option.shift) < Math.abs(best.shift))) {
      best = option
    }
  }
  return best
}

/**
 * Where two boxes line up on one axis, once snapped: the edges they share,
 * or the middle when they share no edge.
 */
function shared(mine: Span, theirs: Span, snapped?: number): number[] {
  const near = (p: number, q: number) => Math.abs(p - q) < 0.5
  const edges = [theirs.a, theirs.b]
  const found = edges.filter((at) => near(at, mine.a) || near(at, mine.b))
  if (found.length) return found
  const middle = (theirs.a + theirs.b) / 2
  if ([mine.a, (mine.a + mine.b) / 2, mine.b].some((at) => near(at, middle))) return [middle]
  // A middle on an edge: the line it snapped to.
  return snapped === undefined ? [] : [snapped]
}

function pick(a: AxisSnap | null, b: AxisSnap | null): AxisSnap | null {
  if (!a) return b
  if (!b) return a
  // A tie goes to alignment: it is the one people are usually aiming for.
  return Math.abs(b.shift) < Math.abs(a.shift) - 0.01 ? b : a
}

export function snapMove(moving: Rect, targets: Rect[], reach: number): Snap {
  const xSnap = pick(alignOn(across(moving), targets.map(across), reach), spaceOn(across(moving), targets.map(across), reach))
  const ySnap = pick(alignOn(down(moving), targets.map(down), reach), spaceOn(down(moving), targets.map(down), reach))
  const dx = xSnap?.shift ?? 0
  const dy = ySnap?.shift ?? 0
  const placed: Rect = { x: moving.x + dx, y: moving.y + dy, w: moving.w, h: moving.h }
  const lines: Segment[] = []
  const gaps: Segment[] = []

  // Lines each way, from the moved box to the one thing it lines up with
  // -- not to everything else that happens to share the line, which on a
  // board of pack shots is most of them and says nothing (Huanrui, 2026-09-30).
  // With that one thing, every edge that now lines up is drawn: two pictures
  // of the same width get both the left and the right line (2026-10-05). The
  // middle is drawn only when no edge lines up, or same-sized pictures would
  // always get three.
  if (xSnap?.kind === 'align' && xSnap.to !== undefined) {
    const t = targets[xSnap.to]!
    for (const at of shared(across(placed), across(t), xSnap.at)) {
      lines.push({ x1: at, y1: Math.min(placed.y, t.y), x2: at, y2: Math.max(placed.y + placed.h, t.y + t.h) })
    }
  }
  if (ySnap?.kind === 'align' && ySnap.to !== undefined) {
    const t = targets[ySnap.to]!
    for (const at of shared(down(placed), down(t), ySnap.at)) {
      lines.push({ x1: Math.min(placed.x, t.x), y1: at, x2: Math.max(placed.x + placed.w, t.x + t.w), y2: at })
    }
  }
  for (const gap of xSnap?.kind === 'space' ? xSnap.gaps ?? [] : []) {
    gaps.push({ x1: gap.from, y1: gap.on, x2: gap.to, y2: gap.on })
  }
  for (const gap of ySnap?.kind === 'space' ? ySnap.gaps ?? [] : []) {
    gaps.push({ x1: gap.on, y1: gap.from, x2: gap.on, y2: gap.to })
  }
  return { dx, dy, lines, gaps }
}

/** The empty space between two boxes; 0 when they touch or overlap. */
export function gapBetween(a: Rect, b: Rect): number {
  const dx = Math.max(0, b.x - (a.x + a.w), a.x - (b.x + b.w))
  const dy = Math.max(0, b.y - (a.y + a.h), a.y - (b.y + b.h))
  return Math.hypot(dx, dy)
}

/**
 * Only what is around it: within `near` of the box, nearest first, and no
 * more than a handful. Lining up with a picture across the board is lining
 * up with something the person is not looking at.
 */
export function around(moving: Rect, targets: Rect[], near: number, most = 8): Rect[] {
  return targets
    .map((t) => ({ t, d: gapBetween(moving, t) }))
    .filter((one) => one.d <= near)
    .sort((p, q) => p.d - q.d)
    .slice(0, most)
    .map((one) => one.t)
}

/**
 * One moving edge -- the side of a picture being resized -- against the edges
 * and middles of what is around it. Answers the correction and the line.
 */
export function snapEdge(
  axis: 'x' | 'y',
  value: number,
  moving: Rect,
  targets: Rect[],
  reach: number,
): { shift: number; line: Segment } | null {
  let best: { shift: number; at: number; t: Rect } | null = null
  for (const t of targets) {
    const lines = axis === 'x' ? [t.x, t.x + t.w / 2, t.x + t.w] : [t.y, t.y + t.h / 2, t.y + t.h]
    for (const at of lines) {
      const shift = at - value
      if (Math.abs(shift) <= reach && (!best || Math.abs(shift) < Math.abs(best.shift) - 0.01)) {
        best = { shift, at, t }
      }
    }
  }
  if (!best) return null
  const { at, t } = best
  return {
    shift: best.shift,
    line:
      axis === 'x'
        ? { x1: at, y1: Math.min(moving.y, t.y), x2: at, y2: Math.max(moving.y + moving.h, t.y + t.h) }
        : { x1: Math.min(moving.x, t.x), y1: at, x2: Math.max(moving.x + moving.w, t.x + t.w), y2: at },
  }
}

/**
 * The gaps between things side by side (axis 'x') or one above the other
 * ('y'): each thing to its nearest neighbour that way, among those it
 * overlaps on the other axis. What a gap being dragged can be made equal to
 * (ProjectCanvas, spaceGroup). Each comes with the span that marks it.
 */
export function neighbourGaps(targets: Rect[], axis: 'x' | 'y'): { gap: number; mark: Segment }[] {
  const spans = targets.map(axis === 'x' ? across : down)
  const out: { gap: number; mark: Segment }[] = []
  spans.forEach((one, i) => {
    let best: { gap: number; j: number } | null = null
    spans.forEach((other, j) => {
      if (i === j) return
      const overlap = Math.min(one.d, other.d) - Math.max(one.c, other.c)
      const gap = other.a - one.b
      if (overlap <= 0 || gap <= 0) return
      if (!best || gap < best.gap) best = { gap, j }
    })
    if (!best) return
    const { gap, j } = best as { gap: number; j: number }
    const other = spans[j]!
    const on = (Math.max(one.c, other.c) + Math.min(one.d, other.d)) / 2
    out.push({
      gap,
      mark:
        axis === 'x'
          ? { x1: one.b, y1: on, x2: other.a, y2: on }
          : { x1: on, y1: one.b, x2: on, y2: other.a },
    })
  })
  return out
}
